import { jobLists } from "character";
import FormComponents from "components/forms/FormComponents";
import Link from "components/link/Link";
import NoCharacter from "components/no-character/NoCharacter";
import { items } from "data-files/items";
import { quests } from "data-files/quests";
import { EorzeaDataType, GameData, InputType, Job, LinkType, RelicData, View } from "enums";
import manager from "managers/app";
import { ViewRelicInfo } from "managers/types/relic-types";
import { Component } from "react";
import { connect } from "react-redux";
import t from "translations/translator";
import { AppState } from "types/app";
import utils from "util/utils";
import ContentHeader from "views/page-parts/content/content-header/ContentHeader";
import DetailsSection from "views/page-parts/content/details-section/DetailsSection";
import Obtained from "views/page-parts/content/obtained/Obtained";
import OverviewTable from "views/page-parts/content/overview-table/OverviewTable";
import { Requirement } from "views/page-parts/content/relic-requirements/types";
import { ViewWeaponAnimaReconditionedProps } from "views/relics/weapons/anima/reconditioned/types";
import { TaskInfo } from "views/type";
import "./WeaponAnimaReconditioned.css";

const mapStateToProps = (state:AppState) => {
	return {
		lang: state.userInfo.options.lang,
		gameLang: state.userInfo.options.gameLang,
		userInfo: state.userInfo,
		hideCompletedSteps: state.userInfo.options.hideCompletedSteps,
		hideCompletedTasks: state.userInfo.options.hideCompletedTasks,
		showMaximumItemQty: state.userInfo.options.showMaximumItemQty,
		showJob: state.userInfo.options.job,
	};
};

class WeaponAnimaReconditioned extends Component<ViewWeaponAnimaReconditionedProps> {
	private requirements:Requirement[] = [];
	private importantNPCs:GameData.NPC[] = [];
	private relicInfo:ViewRelicInfo = {
		jobs: jobLists.relic[RelicData.Group.WEAPON_ANIMA],
		type: RelicData.Type.WEAPON,
		relic: RelicData.Group.WEAPON_ANIMA,
		part: RelicData.Part.WEAPON_RECONDITIONED,
		steps: [RelicData.Step.SAND],
		stepPlaceholders: { [RelicData.Step.SAND]: { ITEM: GameData.Item.CRYSTAL_SAND } },
		next: {
			part: RelicData.Part.WEAPON_SHARPENED,
			view: View.RELIC_WEAPONS_ANIMA_SHARPENED,
		},
		previous: {
			part: RelicData.Part.WEAPON_HYPERCONDUCTIVE,
			view: View.RELIC_WEAPONS_ANIMA_HYPERCONDUCTIVE,
		},
	};

	constructor(props:ViewWeaponAnimaReconditionedProps){
		super(props);
		this.requirements = this.getRequirements();
		this.importantNPCs = this.getImportantNPCs();
	}

	render():JSX.Element{
		const character = manager.data.getActiveCharacter();
		if(character === null){ return <NoCharacter />; }

		let row = "odd-row";

		const completeSteps = manager.content.getCompleteSteps(character.progress, this.relicInfo);
		const taskInfo:TaskInfo[] = [
			{ task: RelicData.Task.TASK_1, target: manager.relics.getTaskItem(RelicData.Step.SAND, RelicData.Task.TASK_1, Job.PLD) },
			{ task: RelicData.Task.TASK_2, target: manager.relics.getTaskItem(RelicData.Step.SAND, RelicData.Task.TASK_2, Job.PLD) },
		];

		return (
			<div id="weapon-anima-reconditioned" className="relic">
				<ContentHeader requirements={this.requirements} importantNPCs={this.importantNPCs} relicInfo={this.relicInfo}>
					<p>{t.t("The %PART% upgrade follows on from %PREVIOUSPART%", { PART: this.relicInfo.part, PREVIOUSPART: this.relicInfo.previous ? this.relicInfo.previous.part : "Unknown"})}</p>
					<p>{t.t("Complete")}<Link type={LinkType.LODESTONE} lodestoneType={EorzeaDataType.BLUEQUEST} text={GameData.Quest.DREAM_FULFILLED} lodestoneID={quests[GameData.Quest.DREAM_FULFILLED].lodestoneID} /></p>
				</ContentHeader>
				<div id="relic-checklist">
					<OverviewTable character={character} completeSteps={completeSteps} relicInfo={this.relicInfo} />
				</div>
				<div id="relic-details">
					<h1>{t.t("Details")}</h1>
					{this.props.hideCompletedSteps && completeSteps.allComplete ? t.t("All Steps Complete") : null }
					<DetailsSection title={RelicData.Step.SAND} placeholders={this.relicInfo.stepPlaceholders[RelicData.Step.SAND]} id={RelicData.Step.SAND} stepComplete={completeSteps[RelicData.Step.SAND]}>
						<p>{t.t("You may receive bonus Treated Crystal Sand when trading in items and therefore may need to do fewer hand ins")}</p>
						<div className="table-wrapper">
							<table className="table">
								<thead><tr><th>{t.t("Obtained")}</th><th>{t.t("Item")}</th><th>{t.t("Quantity / Job")}</th><th>{t.t("Total")}</th><th>{t.t("Used")}</th><th>{t.t("Remaining")}</th><th>{t.t("Inventory")}</th><th>{t.t("To Obtain")}</th></tr></thead>
								<tbody>
									{taskInfo.map((thisTask) => {
										let thisTarget = GameData.Item.UNKNOWN;
										if(utils.guards.isEnumValue(GameData.Item, thisTask.target)){ thisTarget = thisTask.target; }

										const inventory = manager.data.getInventoryStatus({
											item: thisTarget,
											relic: [this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.SAND, thisTask.task],
											getMax: this.props.showMaximumItemQty,
										});

										if(this.props.hideCompletedTasks && inventory.toObtain === 0){ return null; }

										const tableRow = <tr key={`${RelicData.Step.SAND}-${thisTask.task}`} className={row}>
											<td><Obtained obtained={items[thisTarget].obtained} qtyNeeded={inventory.total} /></td>
											<td>{t.t(thisTarget)}</td>
											<td>{inventory.qtyPerJob.toLocaleString()}</td>
											<td>{inventory.total.toLocaleString()}</td>
											<td>{inventory.used.toLocaleString()}</td>
											<td>{inventory.remaining.toLocaleString()}</td>
											<td><FormComponents.TextInput label={{ hide: true }} input={{ type: InputType.NUMBER, value: inventory.inventory, dataAttributes: { item: thisTarget }, events: { onChange: manager.content.inventoryUpdated }}} /></td>
											<td>{inventory.toObtain.toLocaleString()}</td>
										</tr>;
										row = row === "odd-row" ? "even-row" : "odd-row";
										return tableRow;
									})}
									{
										character.progress.isComplete(this.relicInfo.type, this.relicInfo.relic, this.relicInfo.part, RelicData.Step.SAND)
											? <tr className="all-complete"><td colSpan={8}><p>{t.t("All Complete")}</p></td></tr>
											: null
									}
								</tbody>
							</table>
						</div>
					</DetailsSection>
				</div>
			</div>
		);
	}

	componentDidMount(){
		manager.view.changeComplete();
	}

	private getRequirements():Requirement[]{
		if(!this.relicInfo || this.relicInfo.previous === null){ return []; }
		return [
			{ type: "previous part", part: this.relicInfo.previous.part, partView: this.relicInfo.previous.view },
		];
	}

	private getImportantNPCs():GameData.NPC[]{
		return [GameData.NPC.ARDASHIR];
	}
}
export default connect(mapStateToProps)(WeaponAnimaReconditioned);
